/**
 * Tween — minimal substrate-agnostic tween primitive.
 *
 * Phase H-B Stage 4b. We need a tween primitive for the Pixi path
 * because pixi.js (unlike Phaser) doesn't ship one. Rather than pull
 * in gsap or @tweenjs/tween.js, this is a hand-rolled mini-impl
 * tailored to our exact use cases: Sine.easeInOut, Cubic.easeOut,
 * Power2, Back.In; yoyo + repeat counts; onComplete; kill().
 *
 * Phaser-mode consumers can keep using `scene.tweens.add` directly —
 * the API surface differs only at the call site. This Tween is
 * primarily for code paths that need to be substrate-agnostic
 * (PixiInteractableVisual bob; future bodySprite animations
 * via IMobSprite when Stage 4c lands).
 *
 * The driver is whatever ticker you give it. PixiOverlayHost's
 * addTickListener works as a driver out of the box. Headless test
 * runs can call `step(dtMs)` manually.
 */

import { AllocCounters } from '../diagnostics/AllocCounters';

export type EasingFn = (t: number) => number;

export const Easing = {
  linear: (t: number) => t,
  sineInOut: (t: number) => -(Math.cos(Math.PI * t) - 1) / 2,
  sineOut: (t: number) => Math.sin((t * Math.PI) / 2),
  cubicOut: (t: number) => 1 - Math.pow(1 - t, 3),
  power2: (t: number) => t * t,
  /** t² with reversed slope: starts fast, ends slow (Quad.easeOut). */
  power2Out: (t: number) => 1 - (1 - t) * (1 - t),
  /** Symmetric ease: slow ends, fast middle (Quad.easeInOut). */
  power2InOut: (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  backIn: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return c3 * t * t * t - c1 * t * t;
  },
};

export interface TweenOptions {
  /** Map of property name → target value. Reads start values from `target` at construction. */
  to: Record<string, number>;
  /** Forward-leg duration in ms. Yoyo (if enabled) doubles total cycle time. */
  durationMs: number;
  /** Easing function (default: linear). */
  ease?: EasingFn;
  /** Reverse on second half so values return to start. Default false. */
  yoyo?: boolean;
  /** -1 for infinite, 0 for one-shot, N for N additional loops. Default 0. */
  repeat?: number;
  /** Fired exactly once when the LAST loop's last leg ends. */
  onComplete?: () => void;
}

// Tween targets any object whose tweened keys are numbers. Cast through
// a permissive index signature so consumers can pass DOM/Pixi/Phaser
// objects (which have many non-numeric properties) — only the keys
// listed in `to` are read/written.
type TweenTarget = { [k: string]: number };

export class Tween {
  private elapsed = 0;
  private fromValues: Record<string, number> = {};
  private completed = false;
  private cycleCount = 0;
  private target: TweenTarget;
  private opts: TweenOptions;
  // Frame-jitter Wave 1 Phase C iter 3 (2026-05-16) — cache the property
  // names from `opts.to` once at construction. Prior `step()` called
  // `Object.keys(this.opts.to)` every tick (and the for-of allocated an
  // iterator each frame); with ~7 active tween listeners firing per frame
  // (368/sec measured), this was a top-tier per-frame allocator.
  private readonly toKeys: string[];

  constructor(target: object, opts: TweenOptions) {
    this.target = target as TweenTarget;
    this.opts = opts;
    this.toKeys = Object.keys(opts.to);
    for (let i = 0; i < this.toKeys.length; i++) {
      const k = this.toKeys[i];
      this.fromValues[k] = this.target[k] ?? 0;
    }
  }

  /**
   * Advance by `dtMs`. Returns true while the tween is still alive,
   * false once it has completed (call sites can check the return to
   * unsubscribe automatically).
   */
  step(dtMs: number): boolean {
    AllocCounters.inc('tween.step');
    if (this.completed) return false;

    this.elapsed += dtMs;
    const { durationMs, ease = Easing.linear, yoyo = false, repeat = 0 } = this.opts;
    const cycleMs = yoyo ? durationMs * 2 : durationMs;

    while (this.elapsed >= cycleMs) {
      this.elapsed -= cycleMs;
      this.cycleCount++;
      if (repeat !== -1 && this.cycleCount > repeat) {
        // Settle on the final value (yoyo → from; non-yoyo → to)
        for (let i = 0; i < this.toKeys.length; i++) {
          const k = this.toKeys[i];
          this.target[k] = yoyo ? this.fromValues[k] : this.opts.to[k];
        }
        this.completed = true;
        this.opts.onComplete?.();
        return false;
      }
    }

    let phase: number;
    if (yoyo) {
      const halfMs = durationMs;
      if (this.elapsed < halfMs) {
        phase = ease(this.elapsed / halfMs);
      } else {
        phase = ease(1 - (this.elapsed - halfMs) / halfMs);
      }
    } else {
      phase = ease(this.elapsed / durationMs);
    }

    for (let i = 0; i < this.toKeys.length; i++) {
      const k = this.toKeys[i];
      const from = this.fromValues[k];
      const to = this.opts.to[k];
      this.target[k] = from + (to - from) * phase;
    }

    return true;
  }

  /**
   * Cancel mid-flight. The target is left at whatever value it has
   * right now — no settle, no onComplete.
   */
  kill(): void {
    this.completed = true;
  }

  isAlive(): boolean {
    return !this.completed;
  }
}
